import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
	createPipeline,
	updatePipeline,
	deletePipeline,
	restorePipeline,
	createStage,
	updateStage,
	deleteStage,
	reorderStages,
} from '../../api/crmApi';
import { activitiesKeys } from '../activities/keys';
import { dealsKeys } from '../deals/keys';

import { pipelinesKeys, pipelinesMutationKeys } from './keys';

export function useCreatePipeline() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.create,
		mutationFn: createPipeline,
		onSuccess: () => qc.invalidateQueries({ queryKey: pipelinesKeys.all }),
	});
}

export function useUpdatePipeline() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.update,
		mutationFn: ({ id, payload }) => updatePipeline(id, payload),
		// Optimistic update
		onMutate: async ({ id, payload }) => {
			// Cancel in-flight queries to prevent overwriting our optimistic state
			await qc.cancelQueries({ queryKey: pipelinesKeys.list() });

			// Snapshot current state for rollback
			const previousPipelines = qc.getQueryData(pipelinesKeys.list());

			// Optimistically apply changes; enforce single default pipeline
			qc.setQueryData(pipelinesKeys.list(), (old) => {
				if (!Array.isArray(old)) return old;
				return old.map((p) =>
					p.id === id ? { ...p, ...payload } : payload.isDefault ? { ...p, isDefault: false } : p,
				);
			});

			return { previousPipelines };
		},
		// Rollback on server error
		onError: (err, vars, context) => {
			if (context?.previousPipelines) {
				qc.setQueryData(pipelinesKeys.list(), context.previousPipelines);
			}
		},
		// Always refetch to reconcile with server state
		onSettled: () => qc.invalidateQueries({ queryKey: pipelinesKeys.all }),
	});
}

export function useDeletePipeline() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.delete,
		mutationFn: deletePipeline,
		// Invalidate pipelines + related entities (deals/activities belong to pipelines)
		onSettled: () => {
			qc.invalidateQueries({ queryKey: pipelinesKeys.all });
			qc.invalidateQueries({ queryKey: dealsKeys.all });
			qc.invalidateQueries({ queryKey: activitiesKeys.all });
		},
	});
}

export function useRestorePipeline() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.restore,
		mutationFn: restorePipeline,
		// Invalidate pipelines + related entities to reflect restored state
		onSettled: () => {
			qc.invalidateQueries({ queryKey: pipelinesKeys.all });
			qc.invalidateQueries({ queryKey: dealsKeys.all });
			qc.invalidateQueries({ queryKey: activitiesKeys.all });
		},
	});
}

export function useCreateStage() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.createStage,
		mutationFn: ({ pipelineId, payload }) => createStage(pipelineId, payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: pipelinesKeys.all }),
	});
}

export function useUpdateStage() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.updateStage,
		mutationFn: ({ pipelineId, stageId, payload }) => updateStage(pipelineId, stageId, payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: pipelinesKeys.all }),
	});
}

export function useDeleteStage() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.deleteStage,
		mutationFn: ({ pipelineId, stageId }) => deleteStage(pipelineId, stageId),
		// Refetch pipelines to reconcile stage list with server state
		onSettled: () => qc.invalidateQueries({ queryKey: pipelinesKeys.all }),
	});
}

export function useReorderStages() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: pipelinesMutationKeys.reorderStages,
		mutationFn: ({ pipelineId, stageOrders }) => reorderStages(pipelineId, stageOrders),
		onSuccess: () => qc.invalidateQueries({ queryKey: pipelinesKeys.all }),
	});
}
